import { useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Divider, Chip, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Tooltip, Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

const QUESTION_TYPES = ['text', 'yes_no', 'multiple_choice'];

const EMPTY_QUESTION = {
  id: '',
  question: '',
  type: 'text',
  options: [],
  createdByAI: false,
};

const AtsFilteringQuestions = ({ positionId, questions = [], onChange }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const { authToken } = useStore.getState();

  const [newQuestion, setNewQuestion] = useState({ ...EMPTY_QUESTION });
  const [newOption, setNewOption] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [expandedNew, setExpandedNew] = useState(false);

  const handleAddQuestion = () => {
    if (!newQuestion.question.trim()) return;

    const question = {
      ...newQuestion,
      id: uuidv4(),
      options: newQuestion.type === 'multiple_choice' ? newQuestion.options : [],
    };

    onChange([...questions, question]);
    setNewQuestion({ ...EMPTY_QUESTION });
    setNewOption('');
    setExpandedNew(false);
  };

  const handleRemoveQuestion = (questionId) => {
    onChange(questions.filter((q) => q.id !== questionId));
  };

  const handleAddOption = () => {
    if (!newOption.trim()) return;
    setNewQuestion((prev) => ({
      ...prev,
      options: [...prev.options, newOption.trim()],
    }));
    setNewOption('');
  };

  const handleRemoveOption = (index) => {
    setNewQuestion((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleGenerateWithAI = async () => {
    if (!positionId) return;

    setGeneratingAI(true);
    try {
      const response = await api.post(
        `/positions/${positionId}/filtering-questions/generate`,
        { language: lang },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response?.status_response === 'OK') {
        const generated = response.data.response.data.filteringQuestions || [];
        // Agregar a las existentes, evitar duplicados por id
        const existingIds = questions.map((q) => q.id);
        const newOnes = generated.filter((q) => !existingIds.includes(q.id));
        onChange([...questions, ...newOnes]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGeneratingAI(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      text: lang === 'en' ? 'Text' : 'Texto',
      yes_no: lang === 'en' ? 'Yes / No' : 'Sí / No',
      multiple_choice: lang === 'en' ? 'Multiple choice' : 'Opción múltiple',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    if (type === 'yes_no') return 'success';
    if (type === 'multiple_choice') return 'warning';
    return 'default';
  };

  return (
    <Box>

      {/* Header con botón IA */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            {t('ats.positions.wizard.questions.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('ats.positions.wizard.questions.subtitle')}
          </Typography>
        </Box>

        <Tooltip title={!positionId ? t('ats.positions.wizard.questions.aiSaveFirst') : ''}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={generatingAI ? <CircularProgress size={14} /> : <AIIcon />}
              onClick={handleGenerateWithAI}
              disabled={generatingAI || !positionId}
              sx={{
                borderColor: '#6A5AF9',
                color: '#6A5AF9',
                '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' },
              }}
            >
              {generatingAI
                ? t('ats.positions.wizard.questions.generating')
                : t('ats.positions.wizard.questions.generateAI')
              }
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Lista de preguntas existentes */}
      {questions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: '1px dashed #D1D5DB', borderRadius: 2, p: 3, textAlign: 'center', mb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('ats.positions.wizard.questions.empty')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {questions.map((q, index) => (
            <Paper
              key={q.id}
              elevation={0}
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>

                {/* Número */}
                <Box sx={{
                  width: 24, height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#6A5AF9',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  mt: 0.25,
                }}>
                  {index + 1}
                </Box>

                {/* Pregunta */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {q.question}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                      label={getTypeLabel(q.type)}
                      size="small"
                      color={getTypeColor(q.type)}
                      variant="outlined"
                    />
                    {q.createdByAI && (
                      <Chip
                        icon={<AIIcon sx={{ fontSize: '12px !important' }} />}
                        label="IA"
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#6A5AF9', color: '#6A5AF9' }}
                      />
                    )}
                  </Box>
                  {q.type === 'multiple_choice' && q.options.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {q.options.map((opt, i) => (
                        <Chip key={i} label={opt} size="small" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Eliminar */}
                <Tooltip title={t('ats.positions.wizard.questions.remove')}>
                  <IconButton size="small" color="error" onClick={() => handleRemoveQuestion(q.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Agregar nueva pregunta */}
      <Box
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header agregar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            backgroundColor: '#F9FAFB',
            cursor: 'pointer',
          }}
          onClick={() => setExpandedNew((prev) => !prev)}
        >
          <Typography variant="body2" fontWeight={600} color="#6A5AF9">
            {t('ats.positions.wizard.questions.addTitle')}
          </Typography>
          <IconButton size="small">
            {expandedNew ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        <Collapse in={expandedNew}>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Texto de la pregunta */}
            <TextField
              label={t('ats.positions.wizard.questions.questionLabel')}
              value={newQuestion.question}
              onChange={(e) => setNewQuestion((prev) => ({ ...prev, question: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            {/* Tipo */}
            <FormControl size="small" sx={{ width: 240 }}>
              <InputLabel>{t('ats.positions.wizard.questions.typeLabel')}</InputLabel>
              <Select
                value={newQuestion.type}
                label={t('ats.positions.wizard.questions.typeLabel')}
                onChange={(e) => setNewQuestion((prev) => ({
                  ...prev,
                  type: e.target.value,
                  options: [],
                }))}
              >
                {QUESTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {getTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Opciones — solo si es multiple_choice */}
            {newQuestion.type === 'multiple_choice' && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('ats.positions.wizard.questions.optionsLabel')}
                </Typography>

                {newQuestion.options.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {newQuestion.options.map((opt, i) => (
                      <Chip
                        key={i}
                        label={opt}
                        size="small"
                        onDelete={() => handleRemoveOption(i)}
                      />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder={t('ats.positions.wizard.questions.optionPlaceholder')}
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddOption(); }}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddOption}
                    disabled={!newOption.trim()}
                  >
                    {t('ats.positions.wizard.questions.addOption')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Botón agregar pregunta */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddQuestion}
                disabled={
                  !newQuestion.question.trim() ||
                  (newQuestion.type === 'multiple_choice' && newQuestion.options.length < 2)
                }
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
              >
                {t('ats.positions.wizard.questions.addQuestion')}
              </Button>
            </Box>

          </Box>
        </Collapse>
      </Box>

    </Box>
  );
};

export default AtsFilteringQuestions;